import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-heading font-bold text-foreground-900">分类不存在</h2>
            <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hitawa.cn/' },
      { '@type': 'ListItem', position: 2, name: category.name, item: `https://hitawa.cn/category/${category.slug}` },
    ],
  };

  const webpageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 51吃瓜网`,
    description: `51吃瓜网${category.name}频道，共${category.count.toLocaleString()}条爆料内容，每日更新最新吃瓜资讯。`,
    url: `https://hitawa.cn/category/${category.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '51吃瓜网',
      url: 'https://hitawa.cn/',
    },
  };

  return (
    <>
      <Navbar />
      <main>
        <section className="pt-16">
          <div className="relative w-full h-[320px] md:h-[400px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Dark%20atmospheric%20entertainment%20news%20background%20with%20warm%20orange%20gradient%20lighting%2C%20abstract%20modern%20composition%2C%20editorial%20style%2C%20clean%20minimal%20background&width=1800&height=700&seq=category-hero&orientation=landscape"
              alt={category.name}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/80"></div>
            <div className="relative z-10 w-full h-full flex items-center px-4 md:px-6">
              <div>
                <nav className="flex items-center gap-2 text-sm text-white/60 mb-4">
                  <Link to="/" className="hover:text-white transition-colors">首页</Link>
                  <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
                  <span className="text-white">{category.name}</span>
                </nav>
                <h1 className="text-3xl md:text-5xl font-heading font-bold text-white tracking-tight">
                  {category.name}
                </h1>
                <p className="text-sm md:text-base text-white/70 mt-2 max-w-md">
                  共 {category.count.toLocaleString()} 条爆料内容，持续更新中
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50 border-b border-background-200/70 sticky top-16 z-40">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  <i className={`${cat.icon} text-sm w-4 h-4 flex items-center justify-center`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
          <style>{`.scrollbar-hide::-webkit-scrollbar { display: none; }`}</style>
        </section>

        <section className="bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 hover:translate-y-[-4px]"
                >
                  <div className="relative w-full aspect-[16/10] overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    {article.isHot && (
                      <div className="absolute top-3 left-3">
                        <span className="px-2 py-0.5 rounded-full bg-accent-500 text-white text-[10px] font-bold">HOT</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="text-sm md:text-base font-semibold text-foreground-900 line-clamp-2 leading-relaxed group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-xs md:text-sm text-foreground-500 mt-2 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 mt-3 pt-3 border-t border-background-200/60">
                      <span className="flex items-center gap-1 text-[11px] text-foreground-500">
                        <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
                        {(article.views / 10000).toFixed(1)}万
                      </span>
                      <span className="flex items-center gap-1 text-[11px] text-foreground-500">
                        <i className="ri-chat-3-line w-3 h-3 flex items-center justify-center"></i>
                        {(article.comments / 10000).toFixed(1)}万
                      </span>
                      <span className="text-[11px] text-foreground-400 ml-auto">{article.date}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {categoryArticles.length === 0 && (
              <div className="text-center py-16">
                <i className="ri-inbox-line text-4xl text-foreground-300 w-10 h-10 flex items-center justify-center mx-auto"></i>
                <p className="text-foreground-500 mt-4">该分类暂无内容，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageJsonLd) }}
      />
    </>
  );
}